import {
  ANNOTATION_LOCATION,
  ANNOTATION_ORIGIN_LOCATION,
  ANNOTATION_SOURCE_LOCATION,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { BackstageIdentityApi, IdentityApi, OAuthApi, ProfileInfo } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import { graphql } from '@octokit/graphql';
import jsYaml from 'js-yaml';

import {
  DeploymentInfo,
  GET_DEFAULT_BRANCH_NAME,
  GET_REPO_DETAILS_QUERY_TEMPLATE,
  GET_REPO_NAME,
  RemoteState,
  RepoDetails,
} from '../github.service';
import {
  GithubOrigin,
  EmuOrgMode,
  UnifiedGithubServiceConfig,
  GetContentData,
  GithubTeam,
  RepoLookupResult,
} from './unifiedGithubService.types';
import { GithubRepositoryStatus, UnifiedGithubApi } from './unifiedGithubService.ref';

/**
 * Concrete implementation of {@link UnifiedGithubApi}.
 *
 * Instantiate via the {@link unifiedGithubApiRef} factory defined in
 * `packages/devkit/src/services/apis.ts`.
 *
 * @example
 * ```ts
 * new UnifiedGithubService(
 *   githubAuthApi,   // GHEC OAuthApi & BackstageIdentityApi
 *   emuAuthApi,      // EMU OAuthApi & BackstageIdentityApi
 *   identityApi,     // AA identity API (for AAID)
 *   {
 *     apiUrl: config.getString('github.ghec.apiUrl'),
 *     graphUrl: config.getString('github.ghec.graphUrl'),
 *     emuOrgUrl: config.getOptionalString('catalog.emuOrgUrl'),
 *   },
 * )
 * ```
 */
export class UnifiedGithubService implements UnifiedGithubApi {
  private static readonly GITHUB_PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';
  private static readonly LOCATION_ANNOTATIONS = [
    ANNOTATION_ORIGIN_LOCATION,
    ANNOTATION_SOURCE_LOCATION,
    ANNOTATION_LOCATION,
  ];

  constructor(
    private readonly ghecAuthApi: OAuthApi & BackstageIdentityApi,
    private readonly emuAuthApi: OAuthApi & BackstageIdentityApi,
    private readonly identityApi: IdentityApi,
    private readonly config: UnifiedGithubServiceConfig,
  ) {}

  // ── Routing ───────────────────────────────────────────────────────────────

  resolveEntityOrigin({ entity }: { entity: Entity }): GithubOrigin {
    const emuOrg = UnifiedGithubService.parseEmuOrg(this.config.emuOrgUrl);

    const annotationSlug = UnifiedGithubService.parseProjectSlugFromAnnotation(
      entity.metadata.annotations?.[UnifiedGithubService.GITHUB_PROJECT_SLUG_ANNOTATION],
    );

    const locationSlug = UnifiedGithubService.LOCATION_ANNOTATIONS.map(k =>
      UnifiedGithubService.parseProjectSlugFromAnnotation(entity.metadata.annotations?.[k]),
    ).find(Boolean);

    if (emuOrg) {
      if (locationSlug?.startsWith(`${emuOrg}/`) || annotationSlug?.startsWith(`${emuOrg}/`)) {
        return 'emu';
      }
    }

    if (annotationSlug || locationSlug) {
      return 'ghec';
    }

    return 'unknown';
  }

  private isEmuProjectSlug(slug: string | undefined): boolean {
    const emuOrg = UnifiedGithubService.parseEmuOrg(this.config.emuOrgUrl);
    return Boolean(emuOrg && slug?.startsWith(`${emuOrg}/`));
  }

  private resolveOriginByOwner(owner: string): GithubOrigin {
    const emuOrg = UnifiedGithubService.parseEmuOrg(this.config.emuOrgUrl);
    if (emuOrg && owner === emuOrg) {
      return 'emu';
    }
    return 'ghec';
  }

  /** Resolves the current org-routing mode from feature flags. */
  private resolveOrgMode(): EmuOrgMode {
    const emuAuth = this.config.featureFlags.isActive('github-emu-auth');
    if (!emuAuth) return 'ghec';
    const combined = this.config.featureFlags.isActive('github-emu-combined');
    return combined ? 'combined' : 'emu-org';
  }

  /** Returns the GHEC org name from config. */
  private get ghecOrg(): string {
    return this.config.ghecOrg;
  }

  /** Returns the EMU org name derived from the emuOrgUrl config. */
  private get emuOrg(): string {
    return UnifiedGithubService.parseEmuOrg(this.config.emuOrgUrl) ?? '';
  }

  getProjectSlugForEntity({ entity }: { entity: Entity }): string | undefined {
    const annotationSlug = UnifiedGithubService.parseProjectSlugFromAnnotation(
      entity.metadata.annotations?.[UnifiedGithubService.GITHUB_PROJECT_SLUG_ANNOTATION],
    );

    const locationSlug = UnifiedGithubService.LOCATION_ANNOTATIONS.map(k =>
      UnifiedGithubService.parseProjectSlugFromAnnotation(entity.metadata.annotations?.[k]),
    ).find(Boolean);

    const origin = this.resolveEntityOrigin({ entity });

    if (origin === 'emu') {
      // For EMU entities, always prioritize location slug over annotation slug
      // because the location is the authoritative source of the actual GitHub repo
      if (this.isEmuProjectSlug(locationSlug)) {
        return locationSlug;
      }
      if (this.isEmuProjectSlug(annotationSlug)) {
        return annotationSlug;
      }
      // Fallback: prefer location over annotation for EMU entities
      return locationSlug ?? annotationSlug;
    }

    if (origin === 'ghec') {
      if (annotationSlug && !this.isEmuProjectSlug(annotationSlug)) {
        return annotationSlug;
      }
      if (locationSlug && !this.isEmuProjectSlug(locationSlug)) {
        return locationSlug;
      }
      return annotationSlug ?? locationSlug;
    }

    return annotationSlug ?? locationSlug;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private getAuthApi(origin: GithubOrigin): OAuthApi & BackstageIdentityApi {
    return origin === 'emu' ? this.emuAuthApi : this.ghecAuthApi;
  }

  private async createOctokit(origin: GithubOrigin): Promise<Octokit> {
    const token = await this.getAuthApi(origin).getAccessToken();
    return new Octokit({ auth: token, baseUrl: this.config.apiUrl });
  }

  private async graphqlRequest<T>(query: string, variables: Record<string, unknown>, origin: GithubOrigin): Promise<T> {
    const token = await this.getAuthApi(origin).getAccessToken();
    return graphql<T>(query, {
      ...variables,
      baseUrl: this.config.graphUrl,
      headers: { authorization: `token ${token}` },
    });
  }

  private async resolveGithubUsernameForOrigin(githubUsername: string, origin: GithubOrigin): Promise<string> {
    if (origin !== 'emu') {
      return githubUsername;
    }
    const profile = (await this.identityApi.getProfileInfo()) as ProfileInfo & { aaId?: string };
    if (!profile?.aaId) {
      throw new Error('Unable to read your AAID. Please re-login to refresh your session.');
    }
    const aaId = profile.aaId.length === 8 ? profile.aaId.slice(2) : profile.aaId;
    return `${aaId}_aaemu`;
  }

  /** Parses owner and repo from "owner/repo" slug, throwing on bad input. */
  private splitSlug(slug: string): { owner: string; repo: string } {
    const [owner, repo] = slug.split('/');
    if (!owner || !repo) {
      throw new Error(`Invalid GitHub project slug: "${slug}"`);
    }
    return { owner, repo };
  }

  // ── GraphQL – explicit origin ─────────────────────────────────────────────

  isUniqueRepo = async ({
    repoName,
    owner,
    origin,
  }: {
    repoName: string;
    owner?: string;
    origin?: GithubOrigin;
  }): Promise<boolean> => {
    // Explicit owner/origin — bypass flag routing, call directly.
    if (owner !== undefined || origin !== undefined) {
      const resolvedOwner = owner ?? (origin === 'emu' ? this.emuOrg : this.ghecOrg);
      const resolvedOrigin = origin ?? this.resolveOriginByOwner(resolvedOwner);
      try {
        await this.graphqlRequest(GET_REPO_NAME, { owner: resolvedOwner, name: repoName }, resolvedOrigin);
        return false;
      } catch {
        return true;
      }
    }

    const mode = this.resolveOrgMode();

    if (mode === 'combined') {
      const [isUniqueInGhec, isUniqueInEmu] = await Promise.all([
        this.checkRepoUnique(this.ghecOrg, repoName, 'ghec'),
        this.checkRepoUnique(this.emuOrg, repoName, 'emu'),
      ]);
      // false = repo exists; return false if found in either org
      return isUniqueInGhec && isUniqueInEmu;
    }

    const resolvedOwner = mode === 'emu-org' ? this.emuOrg : this.ghecOrg;
    const resolvedOrigin: GithubOrigin = mode === 'emu-org' ? 'emu' : 'ghec';
    return this.checkRepoUnique(resolvedOwner, repoName, resolvedOrigin);
  };

  private checkRepoUnique = async (owner: string, repoName: string, origin: GithubOrigin): Promise<boolean> => {
    try {
      await this.graphqlRequest(GET_REPO_NAME, { owner, name: repoName }, origin);
      return false;
    } catch {
      return true;
    }
  };

  /**
   * Distinguishes a GitHub "not found" response (GraphQL `NOT_FOUND` errors
   * or a REST 404 status) from any other error (auth failures, rate
   * limiting, transport/network errors, 5xx, etc.). Used so callers can
   * avoid silently treating a genuine failure as "repo does not exist".
   */
  private static isNotFoundError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const candidate = error as { status?: number; errors?: { type?: string }[] };
    if (candidate.status === 404) {
      return true;
    }
    return (
      Array.isArray(candidate.errors) &&
      candidate.errors.length > 0 &&
      candidate.errors.every(e => e?.type === 'NOT_FOUND')
    );
  }

  /**
   * Returns whether a repository exists for the given owner/origin. Rethrows
   * any error that is not classifiable as "not found" (see
   * {@link UnifiedGithubService.isNotFoundError}) instead of coercing it to
   * `false`.
   */
  private checkRepoExists = async (owner: string, repoName: string, origin: GithubOrigin): Promise<boolean> => {
    try {
      const result = await this.graphqlRequest<{ repository: { id: string } | null }>(
        GET_REPO_NAME,
        { owner, name: repoName },
        origin,
      );
      return Boolean(result.repository);
    } catch (error) {
      if (UnifiedGithubService.isNotFoundError(error)) {
        return false;
      }
      throw error;
    }
  };

  /**
   * Resolves the owner, origin, and existence of a repository by name. See
   * {@link UnifiedGithubApi.lookupRepo} for routing/error-handling semantics.
   */
  lookupRepo = async ({
    repoName,
    owner,
    origin,
  }: {
    repoName: string;
    owner?: string;
    origin?: GithubOrigin;
  }): Promise<RepoLookupResult> => {
    if (owner !== undefined || origin !== undefined) {
      const resolvedOwner = owner ?? (origin === 'emu' ? this.emuOrg : this.ghecOrg);
      const resolvedOrigin = origin && origin !== 'unknown' ? origin : this.resolveOriginByOwner(resolvedOwner);
      const exists = await this.checkRepoExists(resolvedOwner, repoName, resolvedOrigin);
      return { exists, owner: resolvedOwner, origin: resolvedOrigin };
    }

    const mode = this.resolveOrgMode();

    if (mode === 'combined') {
      const [ghecResult, emuResult] = await Promise.allSettled([
        this.checkRepoExists(this.ghecOrg, repoName, 'ghec'),
        this.checkRepoExists(this.emuOrg, repoName, 'emu'),
      ]);

      // Surface genuine (non "not found") failures rather than silently
      // treating them as "repo does not exist".
      if (ghecResult.status === 'rejected') {
        throw ghecResult.reason;
      }
      if (emuResult.status === 'rejected') {
        throw emuResult.reason;
      }

      if (ghecResult.value) {
        return { exists: true, owner: this.ghecOrg, origin: 'ghec' };
      }
      if (emuResult.value) {
        return { exists: true, owner: this.emuOrg, origin: 'emu' };
      }
      // Not found in either org — GHEC is the default creation target.
      return { exists: false, owner: this.ghecOrg, origin: 'ghec' };
    }

    if (mode === 'emu-org') {
      const exists = await this.checkRepoExists(this.emuOrg, repoName, 'emu');
      return { exists, owner: this.emuOrg, origin: 'emu' };
    }

    // Feature flag disabled (or explicitly 'ghec' mode) — GHEC only.
    const exists = await this.checkRepoExists(this.ghecOrg, repoName, 'ghec');
    return { exists, owner: this.ghecOrg, origin: 'ghec' };
  };

  /**
   * Checks name availability for a fixed destination. See
   * {@link UnifiedGithubApi.lookupRepoForDestination}.
   */
  lookupRepoForDestination = async ({
    repoName,
    owner,
    origin,
  }: {
    repoName: string;
    owner: string;
    origin: GithubOrigin;
  }): Promise<RepoLookupResult> => {
    const resolvedOrigin = origin === 'unknown' ? this.resolveOriginByOwner(owner) : origin;
    const targets: { owner: string; origin: GithubOrigin }[] = [{ owner, origin: resolvedOrigin }];

    // Combined mode intentionally requires names to be unique across both orgs.
    if (this.resolveOrgMode() === 'combined') {
      const other =
        resolvedOrigin === 'emu'
          ? { owner: this.ghecOrg, origin: 'ghec' as const }
          : { owner: this.emuOrg, origin: 'emu' as const };
      if (other.owner && other.owner.toLowerCase() !== owner.toLowerCase()) {
        targets.push(other);
      }
    }

    const results = await Promise.all(
      targets.map(async target => ({
        ...target,
        exists: await this.checkRepoExists(target.owner, repoName, target.origin),
      })),
    );
    return results.find(result => result.exists) ?? { exists: false, owner, origin: resolvedOrigin };
  };

  getRepositoryStatus = async ({
    owner,
    repoName,
    origin,
  }: {
    owner: string;
    repoName: string;
    origin: GithubOrigin;
  }): Promise<GithubRepositoryStatus> => {
    try {
      const result = await this.graphqlRequest<{
        repository: { isArchived: boolean; isLocked: boolean } | null;
      }>(GET_REPO_NAME, { owner, name: repoName }, origin);
      const repository = result.repository;

      if (!repository) return GithubRepositoryStatus.NOT_FOUND;
      if (repository.isArchived && repository.isLocked) return GithubRepositoryStatus.ARCHIVED_AND_LOCKED;
      if (repository.isArchived) return GithubRepositoryStatus.ARCHIVED;
      if (repository.isLocked) return GithubRepositoryStatus.LOCKED;
      return GithubRepositoryStatus.ACTIVE;
    } catch {
      return GithubRepositoryStatus.NOT_FOUND;
    }
  };

  getDefaultBranchName = async ({
    owner,
    repoName,
    origin,
  }: {
    owner: string;
    repoName: string;
    origin: GithubOrigin;
  }): Promise<string> => {
    const result = await this.graphqlRequest<any>(GET_DEFAULT_BRANCH_NAME, { owner, name: repoName }, origin).catch(
      () => {
        throw new Error(`Cannot retrieve default branch name from repo ${repoName}`);
      },
    );
    return result.repository.defaultBranchRef.name as string;
  };

  getDxWork = async ({ origin }: { origin: GithubOrigin }): Promise<object> => {
    const org = origin === 'emu' ? this.emuOrg : this.ghecOrg;
    const dxWorkQuery = `
query dxWork($after: String) {
  search(query: "topic:developer-experience org:${org} archived:false", type: REPOSITORY, first: 100, after: $after) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        ... on Repository {
          name
          url
          id
          isArchived
          isLocked
          pullRequests(first: 100, states: OPEN) {
            edges {
              node {
                title
                state
                url
                lastEditedAt
                createdAt
                id
                isDraft
              }
            }
          }
        }
      }
    }
  }
}`;
    const edges: unknown[] = [];
    let after: string | null = null;
    let pageInfo = { hasNextPage: false, endCursor: null as string | null };

    try {
      do {
        const result = await this.graphqlRequest<{
          search: {
            pageInfo: { hasNextPage: boolean; endCursor: string | null };
            edges: { node?: { isArchived?: boolean; isLocked?: boolean } }[];
          };
        }>(dxWorkQuery, { after }, origin);
        pageInfo = result.search.pageInfo;
        edges.push(...result.search.edges.filter(edge => !edge.node?.isArchived && !edge.node?.isLocked));
        after = pageInfo.endCursor;
      } while (pageInfo.hasNextPage);
    } catch {
      throw new Error('Cannot retrieve dx prs');
    }

    return { search: { edges } };
  };

  getUsersGithubTeams = async ({
    githubUsername,
    origin,
  }: {
    githubUsername: string;
    origin?: GithubOrigin;
  }): Promise<GithubTeam[]> => {
    // Explicit origin — bypass flag routing, but still resolve the correct org.
    if (origin !== undefined) {
      const org = origin === 'emu' ? this.emuOrg : this.ghecOrg;
      const username = await this.resolveGithubUsernameForOrigin(githubUsername, origin);
      return this.fetchTeams(username, org, origin);
    }

    const mode = this.resolveOrgMode();

    if (mode === 'combined') {
      const resolvedEmuUsername = await this.resolveGithubUsernameForOrigin(githubUsername, 'emu');
      const [ghecTeams, emuTeams] = await Promise.all([
        this.fetchTeams(githubUsername, this.ghecOrg, 'ghec'),
        this.fetchTeams(resolvedEmuUsername, this.emuOrg, 'emu'),
      ]);
      return [...ghecTeams, ...emuTeams.map(t => ({ ...t, name: `${t.name} (emu)` }))];
    }

    if (mode === 'emu-org') {
      const resolvedEmuUsername = await this.resolveGithubUsernameForOrigin(githubUsername, 'emu');
      const teams = await this.fetchTeams(resolvedEmuUsername, this.emuOrg, 'emu');
      return teams.map(t => ({ ...t, name: `${t.name} (emu)` }));
    }

    return this.fetchTeams(githubUsername, this.ghecOrg, 'ghec');
  };

  private static readonly GET_USER_GITHUB_TEAMS_QUERY = `
    query githubTeams($org: String!, $githubUsername: String!) {
      organization(login: $org) {
        teams(first: 100, userLogins: [$githubUsername]) {
          totalCount
          edges {
            node {
              name
              id
              slug
            }
          }
        }
      }
    }
  `;

  private fetchTeams = async (githubUsername: string, org: string, origin: GithubOrigin): Promise<GithubTeam[]> => {
    const result = await this.graphqlRequest<any>(
      UnifiedGithubService.GET_USER_GITHUB_TEAMS_QUERY,
      { org, githubUsername },
      origin,
    );
    return (
      result?.organization?.teams?.edges?.map((team: { node: { name: string; id: string; slug?: string } }) => ({
        ...team.node,
        org,
        origin,
      })) ?? []
    );
  };

  projectsDetailsQuery = async ({
    owner,
    repoName,
    origin,
  }: {
    owner: string;
    repoName: string;
    origin: GithubOrigin;
  }): Promise<RepoDetails> => {
    const result = await this.graphqlRequest<any>(GET_REPO_DETAILS_QUERY_TEMPLATE, { owner, name: repoName }, origin);
    if (result.repository.isArchived || result.repository.isLocked) {
      throw new Error(`Repository ${owner}/${repoName} is archived or locked`);
    }

    const repo = {
      id: result.repository.id,
      name: repoName,
      description: result.repository.description,
      full_name: `${owner}/${repoName}`,
      owner,
      html_url: result.repository.url,
    };

    const devDeploymentInfo: DeploymentInfo = UnifiedGithubService.tryParseYaml(
      result.repository.runwayMetadata?.text || result.repository.devDeploymentInfo?.text,
    );
    const prodDeploymentInfo: DeploymentInfo = UnifiedGithubService.tryParseYaml(
      result.repository.prodDeploymentInfo?.text,
    );
    const infrastructure: any = UnifiedGithubService.tryParseYaml(result.repository.infrastructure?.text);
    const managedPipelineProperties: any = UnifiedGithubService.tryParseYaml(
      result.repository.managedPipelineProperties?.text,
    );

    let devRemoteState: RemoteState | undefined;
    if (infrastructure) {
      const rs = infrastructure?.['remote-state'];
      devRemoteState = {
        stateFile: rs?.key,
        container: rs?.container_name,
        resourceGroup: rs?.resource_group_name,
        storageAccount: rs?.storage_account_name,
      };
    } else if (managedPipelineProperties?.deployInfra === 'terraform') {
      const mpRs = managedPipelineProperties?.CD?.deployments?.dev?.azurerm;
      devRemoteState = {
        stateFile: mpRs?.statefile,
        container: mpRs?.container_name,
        resourceGroup: mpRs?.resource_group_name,
        storageAccount: mpRs?.storage_account_name,
      };
    }

    let prodRemoteState: RemoteState | undefined;
    if (managedPipelineProperties?.deployInfra === 'terraform') {
      const mpRs = managedPipelineProperties?.CD?.deployments?.prod?.azurerm;
      prodRemoteState = {
        stateFile: mpRs?.statefile,
        container: mpRs?.container_name,
        resourceGroup: mpRs?.resource_group_name,
        storageAccount: mpRs?.storage_account_name,
      };
    }

    return {
      repo,
      readme: result.repository.readme?.text ?? undefined,
      dev: {
        deploymentInfo: devDeploymentInfo ?? undefined,
        remoteState: devRemoteState,
      },
      prod: {
        deploymentInfo: prodDeploymentInfo ?? undefined,
        remoteState: prodRemoteState,
      },
      managedPipelineProperties,
    };
  };

  getLink = ({
    env,
    managedPipelineProperties,
  }: {
    env: 'dev' | 'prod';
    managedPipelineProperties: any;
  }): string | null => {
    const appName = managedPipelineProperties?.CD?.deployments?.[env]?.app?.[0]?.name;
    if (!appName) {
      return null;
    }
    if (managedPipelineProperties?.deployApp === 'appservice') {
      return `https://${appName}.azurewebsites.net`;
    }
    if (managedPipelineProperties?.deployApp === 'storage') {
      return `https://${appName}.z13.web.core.windows.net`;
    }
    return null;
  };

  // ── REST (Octokit) – explicit origin ──────────────────────────────────────

  getCollaboratorPermissionLevel = async ({
    owner,
    repo,
    username,
    origin,
  }: {
    owner: string;
    repo: string;
    username: string;
    origin: GithubOrigin;
  }): Promise<string> => {
    const octokit = await this.createOctokit(origin);
    const { data } = await octokit.repos.getCollaboratorPermissionLevel({ owner, repo, username });
    return data.permission;
  };

  getDefaultBranch = async ({
    owner,
    repo,
    origin,
  }: {
    owner: string;
    repo: string;
    origin: GithubOrigin;
  }): Promise<string> => {
    const octokit = await this.createOctokit(origin);
    const { data } = await octokit.repos.get({ owner, repo });
    return data.default_branch;
  };

  checkFileExists = async ({
    owner,
    repo,
    filePath,
    origin,
  }: {
    owner: string;
    repo: string;
    filePath: string;
    origin: GithubOrigin;
  }): Promise<boolean> => {
    const octokit = await this.createOctokit(origin);
    try {
      await octokit.repos.getContent({ owner, repo, path: filePath });
      return true;
    } catch (error: any) {
      if (error.status === 404) {
        return false;
      }
      throw error;
    }
  };

  // ── Entity-aware convenience wrappers ─────────────────────────────────────

  isUniqueRepoForEntity = async ({ entity }: { entity: Entity }): Promise<boolean> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    // Pass explicit owner+origin so entity-resolved routing overrides flag-based routing.
    return this.isUniqueRepo({ repoName: repo, owner, origin });
  };

  getDefaultBranchNameForEntity = async ({ entity }: { entity: Entity }): Promise<string> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.getDefaultBranchName({ owner, repoName: repo, origin });
  };

  projectsDetailsQueryForEntity = async ({ entity }: { entity: Entity }): Promise<RepoDetails> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.projectsDetailsQuery({ owner, repoName: repo, origin });
  };

  getCollaboratorPermissionLevelForEntity = async ({
    entity,
    username,
  }: {
    entity: Entity;
    username: string;
  }): Promise<string> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.getCollaboratorPermissionLevel({ owner, repo, username, origin });
  };

  getCurrentUsernameForEntity = async ({ entity }: { entity: Entity }): Promise<string> => {
    const origin = this.resolveEntityOrigin({ entity });

    if (origin === 'emu') {
      const profile = (await this.identityApi.getProfileInfo()) as ProfileInfo & { aaId?: string };
      if (!profile?.aaId) {
        throw new Error('Unable to read your AAID. Please re-login to refresh your session.');
      }
      const aaId = profile.aaId.length === 8 ? profile.aaId.slice(2) : profile.aaId;
      return `${aaId}_aaemu`;
    }

    const backstageIdentity = await this.ghecAuthApi.getBackstageIdentity();
    if (!backstageIdentity?.identity.userEntityRef) {
      throw new Error('Unable to read your username. Please re-login to refresh GitHub auth.');
    }
    const { name } = parseEntityRef(backstageIdentity.identity.userEntityRef);
    return name;
  };

  getDefaultBranchForEntity = async ({ entity }: { entity: Entity }): Promise<string> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.getDefaultBranch({ owner, repo, origin });
  };

  checkFileExistsForEntity = async ({ entity, filePath }: { entity: Entity; filePath: string }): Promise<boolean> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.checkFileExists({ owner, repo, filePath, origin });
  };

  // ── File & property helpers ───────────────────────────────────────────────

  fetchFileContent = async ({
    owner,
    repo,
    filePath,
    origin,
  }: {
    owner: string;
    repo: string;
    filePath: string;
    origin: GithubOrigin;
  }): Promise<string | undefined> => {
    const result = await this.graphqlRequest<{ repository: { object?: { text?: string } } }>(
      `query($owner: String!, $repo: String!, $expression: String!) {
        repository(owner: $owner, name: $repo) {
          object(expression: $expression) {
            ... on Blob { text }
          }
        }
      }`,
      { owner, repo, expression: `HEAD:${filePath}` },
      origin,
    );
    return result.repository.object?.text;
  };

  listRepoCustomProperties = async ({
    owner,
    repo,
    origin,
  }: {
    owner: string;
    repo: string;
    origin: GithubOrigin;
  }): Promise<{ property_name: string; value: any }[]> => {
    const octokit = await this.createOctokit(origin);
    const resp = await octokit.request('GET /repos/{owner}/{repo}/properties/values', {
      owner,
      repo,
      headers: { 'X-GitHub-Api-Version': '2022-11-28' },
    });
    return resp.data as { property_name: string; value: any }[];
  };

  archiveRepo = async ({
    owner,
    repo,
    origin,
  }: {
    owner: string;
    repo: string;
    origin: GithubOrigin;
  }): Promise<void> => {
    const octokit = await this.createOctokit(origin);
    await octokit.repos.update({ owner, repo, archived: true });
  };

  deleteRepo = async ({
    owner,
    repo,
    origin,
  }: {
    owner: string;
    repo: string;
    origin: GithubOrigin;
  }): Promise<void> => {
    const octokit = await this.createOctokit(origin);
    await octokit.repos.delete({ owner, repo });
  };

  fetchFileContentForEntity = async ({
    entity,
    filePath,
  }: {
    entity: Entity;
    filePath: string;
  }): Promise<string | undefined> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.fetchFileContent({ owner, repo, filePath, origin });
  };

  listRepoCustomPropertiesForEntity = async ({
    entity,
  }: {
    entity: Entity;
  }): Promise<{ property_name: string; value: any }[]> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.listRepoCustomProperties({ owner, repo, origin });
  };

  archiveRepoForEntity = async ({ entity }: { entity: Entity }): Promise<void> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.archiveRepo({ owner, repo, origin });
  };

  deleteRepoForEntity = async ({ entity }: { entity: Entity }): Promise<void> => {
    const slug = this.getProjectSlugForEntity({ entity });
    if (!slug) {
      throw new Error('Entity does not have a resolvable GitHub project slug.');
    }
    const { owner, repo } = this.splitSlug(slug);
    const origin = this.resolveEntityOrigin({ entity });
    return this.deleteRepo({ owner, repo, origin });
  };

  // ── Write-path operations ─────────────────────────────────────────────────

  createBranch = async ({
    owner,
    repo,
    branchName,
    fromRef,
  }: {
    owner: string;
    repo: string;
    branchName: string;
    fromRef: string;
  }): Promise<void> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    const { data: baseRef } = await octokit.git.getRef({ owner, repo, ref: fromRef });
    await octokit.git.createRef({
      owner,
      repo,
      ref: `refs/heads/${branchName}`,
      sha: baseRef.object.sha,
    });
  };

  createFile = async ({
    owner,
    repo,
    path,
    message,
    content,
    branch,
  }: {
    owner: string;
    repo: string;
    path: string;
    message: string;
    content: string;
    branch: string;
  }): Promise<{ sha: string }> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    const { data } = await octokit.repos.createOrUpdateFileContents({
      owner,
      repo,
      path,
      message,
      content,
      branch,
    });
    return { sha: data.content?.sha ?? '' };
  };

  updateFile = async ({
    owner,
    repo,
    path,
    message,
    content,
    sha,
    branch,
  }: {
    owner: string;
    repo: string;
    path: string;
    message: string;
    content: string;
    sha: string;
    branch: string;
  }): Promise<{ sha: string }> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    const { data } = await octokit.repos.createOrUpdateFileContents({
      owner,
      repo,
      path,
      message,
      content,
      sha,
      branch,
    });
    return { sha: data.content?.sha ?? '' };
  };

  openPullRequest = async ({
    owner,
    repo,
    title,
    head,
    base,
    body,
  }: {
    owner: string;
    repo: string;
    title: string;
    head: string;
    base: string;
    body?: string;
  }): Promise<{ number: number; html_url: string; user: { login: string } | null }> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    const { data } = await octokit.pulls.create({ owner, repo, title, head, base, body });
    return data;
  };

  requestReviewers = async ({
    owner,
    repo,
    pullNumber,
    reviewers,
    teamReviewers,
  }: {
    owner: string;
    repo: string;
    pullNumber: number;
    reviewers: string[];
    teamReviewers: string[];
  }): Promise<void> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    await octokit.pulls.requestReviewers({
      owner,
      repo,
      pull_number: pullNumber,
      reviewers,
      team_reviewers: teamReviewers,
    });
  };

  repositoryDispatch = async ({
    owner,
    repo,
    eventType,
    clientPayload,
  }: {
    owner: string;
    repo: string;
    eventType: string;
    clientPayload: Record<string, unknown>;
  }): Promise<void> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    await octokit.repos.createDispatchEvent({ owner, repo, event_type: eventType, client_payload: clientPayload });
  };

  getFileInfo = async ({
    owner,
    repo,
    filePath,
    ref,
  }: {
    owner: string;
    repo: string;
    filePath: string;
    ref: string;
  }): Promise<GetContentData> => {
    const octokit = await this.createOctokit(this.resolveOriginByOwner(owner));
    const { data } = await octokit.repos.getContent({ owner, repo, path: filePath, ref });
    return data;
  };

  // ── Private utilities ─────────────────────────────────────────────────────

  /**
   * Strips Backstage location ref prefixes (e.g. `url:`, `github:`,
   * `bitbucket:`) and extracts "owner/repo" from a raw annotation value.
   * Returns `undefined` for non-GitHub URLs so that only github.com slugs
   * are used for origin detection and routing.
   */
  private static parseProjectSlugFromAnnotation(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }
    // Strip Backstage location ref prefixes that wrap an http(s) URL,
    // e.g. `url:https://...`, `github:https://...`, `bitbucket:https://...`
    const sanitized = value.replace(/^[a-z][a-z0-9+.-]*:(https?:\/\/)/, '$1');
    const directMatch = sanitized.match(/^([^/]+)\/([^/]+)$/);
    if (directMatch) {
      return `${directMatch[1]}/${directMatch[2].replace(/\.git$/, '')}`;
    }
    try {
      const parsed = new URL(sanitized);
      if (parsed.hostname !== 'github.com') {
        return undefined;
      }
      const [owner, repo] = parsed.pathname.split('/').filter(Boolean);
      if (!owner || !repo) {
        return undefined;
      }
      return `${owner}/${repo.replace(/\.git$/, '')}`;
    } catch {
      return undefined;
    }
  }

  /** Extracts the org name from a GitHub org URL (e.g. "https://github.com/MyOrg/**" → "MyOrg"). */
  private static parseEmuOrg(emuOrgUrl?: string): string | undefined {
    if (!emuOrgUrl) {
      return undefined;
    }
    const sanitized = emuOrgUrl.replace(/\/\*\*$/, '');
    try {
      const parsed = new URL(sanitized);
      return parsed.pathname.split('/').filter(Boolean)[0];
    } catch {
      return undefined;
    }
  }

  private static tryParseYaml(text: string): any {
    try {
      return text ? jsYaml.load(text) : undefined;
    } catch {
      return undefined;
    }
  }
}
